import React, {
  forwardRef,
  useContext,
  useState,
  useRef,
  useEffect,
} from "react";
import PropTypes from "prop-types";

import styles from "./Tabs.scss";
import { TabsContext } from "./TabsContext";

const Tab = forwardRef((props, forwardedRef) => {
  const {
    children,
    disabled,
    actionCb,
    onClick,
    tabIndex,
    ...restProps
  } = props;
  const {
    tabsId,
    addTabRef,
    changeIndex,
    changeTab,
    currentIndex,
    controlledPosition,
    statePosition,
    moveToCurrent,
  } = useContext(TabsContext) || {};
  const [touchStartX, setTouchStartX] = useState(undefined);
  const buttonRef = useRef();
  const indexRef = useRef(tabIndex);

  useEffect(() => {
    addTabRef(buttonRef, indexRef);
  }, []);

  const setRef = (element) => {
    buttonRef.current = element;
    if (typeof forwardedRef === "function") {
      forwardedRef(element);
    } else if (forwardedRef) {
      // eslint-disable-next-line no-param-reassign
      forwardedRef.current = element;
    }
  };

  const changeTabByKey = (event, index) => {
    changeTab(event.key, index);
  };

  useEffect(() => {
    statePosition(null, { x: 0 });
    setTimeout(moveToCurrent, 10);
  }, [children]);

  return (
    <button
      ref={setRef}
      draggable="false"
      type="button"
      className={styles.tab}
      id={`${tabsId}_tab-${tabIndex}`}
      role="tab"
      aria-controls={`${tabsId}_panel-${tabIndex}`}
      aria-selected={tabIndex === currentIndex}
      tabIndex={tabIndex === currentIndex ? 0 : -1}
      onClick={(event) => {
        changeIndex(tabIndex);
        if (actionCb) {
          actionCb(event, tabIndex);
        }
        if (onClick) {
          onClick(event, tabIndex);
        }
      }}
      onTouchStart={() => {
        setTouchStartX(controlledPosition);
      }}
      onTouchEnd={(event) => {
        if (controlledPosition === touchStartX) {
          changeIndex(tabIndex);
          if (actionCb) {
            actionCb(event, tabIndex);
          }
          if (onClick) {
            onClick(event, tabIndex);
          }
        }
        setTouchStartX(undefined);
      }}
      onKeyUp={(event) => {
        changeTabByKey(event, tabIndex, changeTab);
        if (actionCb) {
          actionCb(event, tabIndex);
        }
        if (onClick) {
          onClick(event, tabIndex);
        }
      }}
      disabled={disabled ? true : null}
      {...restProps}
    >
      {children}
    </button>
  );
});

Tab.displayName = "Tabs.Tab";

Tab.propTypes = {
  disabled: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  actionCb: PropTypes.func,
  onClick: PropTypes.func,
  tabIndex: PropTypes.number,
};

Tab.defaultProps = {
  disabled: false,
  actionCb: undefined,
  onClick: undefined,
  tabIndex: 0,
};

export default Tab;
